<template>
    <ag-grid-vue class="bg-white full-width ag-theme-material"
                 :style="computedStyles"
                 :dom-layout="computedDomLayout"
                 :default-col-def="defaultColDef"
                 :column-defs="columnDefs"
                 :row-model-type="computedRowModel"
                 :datasource="datasource"
                 :row-data="rowData"
                 :grid-options="gridOptions"
                 :locale-text="localeText"
                 enable-server-side-sorting
                 suppress-multi-sort
                 enable-server-side-filter
                 enable-col-resize
                 :maxConcurrentDatasourceRequests="1"
                 :grid-ready="onGridReady"
                 :infinite-initial-row-count="initialRowCount || perPage"
                 :column-visible="mayNeedAdjustColsWidth"
                 :column-resized="mayNeedAdjustColsWidth"
                 :grid-size-changed="mayNeedAdjustColsWidth">
    </ag-grid-vue>
</template>

<script>
    import {AgGridVue} from 'ag-grid-vue'
    import axios from 'axios'

    export default {
        name: 'vTable',
        components: {
            AgGridVue
        },
        props: {
            height: {
                type: [Number, String],
                default: '100%'
            },
            columnDefs: {
                type: Array,
                require: true
            },
            resourcePath: {
                type: String,
                require: true
            },
            visibleColumns: {
                type: Array,
                default: () => []
            },
            filter: String,
            initialRowCount: Number,
            heightRowThreshold: Number,
            perPage: {
                type: Number,
                default: 100
            },
            defaultColDef: {
                type: Object,
                default() {
                    return {
                        minWidth: 100
                    }
                }
            },
            context: {
                type: Object,
                default() {
                    return this
                }
            },
            rowData: Array,
            datasource: {
                type: Object,
                default() {
                    return {
                        getRows: function ({
                                               // The first row index to get.
                                               startRow,
                                               // The first row index to NOT get.
                                               endRow,
                                               // If doing Server-side sorting, contains the sort model
                                               sortModel,
                                               // If doing Server-side filtering, contains the filter model
                                               filterModel,
                                               // The grid context object
                                               context,
                                               // Callback to call when the request is successful.
                                               successCallback,
                                               // Callback to call when the request fails.
                                               failCallback
                                           }) {
                            let params = {
                                page: Math.ceil(endRow / this.perPage),
                                sort_by: sortModel && sortModel[0] ? sortModel[0].colId : '',
                                descending: sortModel && sortModel[0] ? sortModel[0].sort === 'desc' : '',
                                per_page: this.perPage,
                                search: this.filter
                            }

                            axios.get(this.resourcePath, {params})
                                .then(response => {
                                    // Callback to call when the request is successful.
                                    // successCallback(rowsThisBlock: any[], lastRow?: number): void
                                    successCallback(response.data.data, response.data.meta.total)
                                })
                                .catch(() => {
                                    // Callback to call when the request fails.
                                    // failCallback(): void
                                    failCallback()
                                })
                        }.bind(this)
                    }
                }
            }
        },
        watch: {
            visibleColumns(newValue) {
                let columnsToHide = this.columnDefs.filter(column => {
                    return !column.lockVisible && !newValue.includes(column.colId)
                }).map(column => column.colId)
                let columnsToShow = this.columnDefs.filter(column => {
                    return column.lockVisible || newValue.includes(column.colId)
                }).map(column => column.colId)
                this.columnApi.setColumnsVisible(columnsToHide, false)
                this.columnApi.setColumnsVisible(columnsToShow, true)
            },
            filter: 'refresh'
        },
        data() {
            return {
                gridOptions: {
                    context: this.context
                },
                gridApi: null,
                columnApi: null
            }
        },
        computed: {
            computedStyles() {
                if (this.heightRowThreshold && this.rowData &&
                    this.rowData.length <= this.heightRowThreshold)
                    return {}

                return {
                    height: this.height
                }
            },
            computedDomLayout() {
                if (this.computedStyles.height)
                    return 'normal'

                return 'autoHeight'
            },
            computedRowModel() {
                if (this.rowData)
                    return 'clientSide'

                return 'infinite'
            },
            localeText() {
                return {
                    // for filter panel
                    page: 'Page',
                    more: 'More',
                    to: 'To',
                    of: 'Of',
                    next: 'Next',
                    last: 'Last',
                    first: 'First',
                    previous: 'Previous',
                    loadingOoo: 'Loading...',

                    // for set filter
                    selectAll: 'Select All',
                    searchOoo: 'Search...',
                    blanks: 'Blanks',

                    // for number filter and text filter
                    filterOoo: 'Filter...',
                    applyFilter: 'Apply filter...',

                    // for number filter
                    equals: 'Equals',
                    lessThan: 'Less Than',
                    greaterThan: 'Greater Than',

                    // for text filter
                    contains: 'Contains',
                    startsWith: 'Starts with',
                    endsWith: 'Ends with',

                    // the header of the default group column
                    group: 'Group',

                    // tool panel
                    columns: 'Columns',
                    rowGroupColumns: 'Pivot columns',
                    rowGroupColumnsEmptyMessage: 'drag columns to group',
                    valueColumns: 'Value columns',
                    pivotMode: 'Pivot mode',
                    groups: 'Groups',
                    values: 'Values',
                    pivots: 'Pivots',
                    valueColumnsEmptyMessage: 'drag columns to aggregate',
                    pivotColumnsEmptyMessage: 'drag here to pivot',
                    toolPanelButton: 'tool panel',

                    // other
                    noRowsToShow: this.$T('no data available to show.'),

                    // enterprise menu
                    pinColumn: 'Pin Column',
                    valueAggregation: 'Value Aggregation',
                    autosizeThiscolumn: 'Autosize this column',
                    autosizeAllColumns: 'Autsoie all columns',
                    groupBy: 'Group by',
                    ungroupBy: 'UnGroup by',
                    resetColumns: 'Reset columns',
                    expandAll: 'Expand all',
                    collapseAll: 'Collapse all',
                    toolPanel: 'Tool panel',
                    export: 'Export',
                    csvExport: 'CSV Export',
                    excelExport: 'Excel Export',

                    // enterprise menu pinning
                    pinLeft: 'Pin left',
                    pinRight: 'Pin right',
                    noPin: 'No pin',

                    // enterprise menu aggregation and status bar
                    sum: 'Sum',
                    min: 'Min',
                    max: 'Max',
                    first: 'First',
                    last: 'Last',
                    none: 'None',
                    count: 'Count',
                    average: 'Average',

                    // standard menu
                    copy: 'Copy',
                    copyWithHeaders: 'Copy With headers',
                    ctrlC: 'ctrl + c',
                    paste: 'paste',
                    ctrlV: 'ctrl + v'
                }
            }
        },
        methods: {
            onGridReady(params) {
                this.gridApi = params.api
                this.columnApi = params.columnApi

                this.$emit('ready')
            },
            mayNeedAdjustColsWidth() {
                let totalWidth = this.columnApi.columnController.allDisplayedColumns
                    .map(column => column.actualWidth)
                    .reduce((total, actual) => total + actual)
                if (totalWidth < this.gridApi.gridPanel.eBody.offsetWidth)
                    this.gridApi.sizeColumnsToFit()
            },
            refresh() {
                this.gridApi.refreshInfiniteCache()
            }
        }
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/Table.vue